import { useCallback, useContext, useMemo } from 'react';
import {
  CLEAR_UNREAD_MESSAGE,
  GET_JOINGAME,
  GET_PLAYERMOVE,
  GET_TOURNAMENT,
  LEAVE_ROOM,
  ONLINE,
  POST_BLIND,
  PRESELECT_ACTION,
  REGISTER_SOCKET,
  RESET_MTT_INACTIVITY,
  RETURN_TABLE,
  SITOUT,
  STRADDLE_PRE_ACTION,
  SUBCRIBE_TO_GAME_UPDATES,
  TEXT_CHAT,
  UNSUBCRIBE_TO_GAME_UPDATES,
  VERIFY_PRIVATE_TABLE_PASSWORD,
  VOTE_MULTI_RUN,
  WAIT_FOR_BB
} from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import GameContext from 'contexts/GameContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useTableId, useTableSelector } from 'hooks/useTableSelector';
import { setSeatJoinError, showJoinButton } from 'reducers/currentTable';
import { clearLiveChatNotification, ReadThrough } from 'reducers/liveChatNotifications';
import { buildJoinGameParams } from 'services/joinGameParams';
import { rememberSeatJoin } from 'services/pendingSeatJoin';
import { markAddOnInitiated } from 'utils/addOnInitiator';
import { convertToNumber, encode, getDeviceId } from 'utils/functions';
import { pauseHandHistorySession } from 'utils/handHistorySession';
import { isValidTournamentId } from 'utils/tournamentSubscriptions';

const EMIT_TIMEOUT = 2000; //2 seconds

export const JOIN_OFFLINE_MESSAGE =
  "You're not connected right now, so your buy-in wasn't sent. Try again once you're back online.";

const reportUnsentAction = (
  reason: 'socket-unavailable' | 'socket-disconnected' | 'table-identity-incomplete',
  gameID: string | number | undefined,
  game: string | undefined,
  timeoutCallback?: () => void
) => {
  // Keep this PII-free: the table identity is enough to distinguish a local
  // transport failure from an unhydrated panel attempting to emit.
  // eslint-disable-next-line no-console
  console.warn('[GameService] Player action not sent', { reason, gameID, game });
  timeoutCallback?.();
};

// Reference count of active tournament-detail subscriptions per tournamentId,
// shared across ALL useGameService callers (module scope). Multiple surfaces
// can hold the same tournament concurrently (App-level open-table set, lobby
// info modal, in-game info panel, per-grid-cell). One `subscribeTournament`
// serves all of them — the ref-count makes sure we ONLY emit the socket
// subscribe on the 0→1 transition and ONLY emit the socket unsubscribe on the
// 1→0 transition. Without this, closing / observing away from ONE surface
// would fire unsubscribeTournament and kill gotTnmStatus for every OTHER
// surface that was still holding the tournament.
//
// The old deferred-teardown machinery (30 s setTimeout) was removed once the
// App-level subscribe path became SET-BASED — an in-tournament balance now
// keeps the joined set string stable across the move (both A and B have the
// same tournamentId), so the diff-effect in App.tsx doesn't fire an unsubscribe
// mid-move in the first place. The counted teardown here is now immediate.
const tournamentSubCounts: Record<string, number> = {};

const useGameService = () => {
  const { socket } = useContext(SocketContext);
  const dispatch = useAppDispatch();

  const {
    openAddChips,
    tableCards,
    setTableCards,
    playerMultiRunVote,
    setIsBlindPosted,
    setOpenAddChips,
    setPlayerMultiRunVote
  } = useContext(GameContext);

  // A mounted table panel owns the identity of every command it emits. The URL
  // only identifies which panel the navigation layer is showing; warmed and
  // kept-alive sibling panels remain mounted and must never inherit that global
  // identity. If a contextual panel has not hydrated, its game type remains
  // empty and table commands fail closed instead of borrowing another panel's.
  const { activeTableInfo } = useContext(ViewManagerContext);
  const tableIdFromContext = useTableId();
  const gameTypeFromTable = useTableSelector((table) => table.game?.game);
  const tableId = tableIdFromContext || activeTableInfo?.gameID;
  const gameType = tableIdFromContext
    ? gameTypeFromTable
    : gameTypeFromTable || activeTableInfo?.gameType;

  const guid = useAppSelector((state) => state.user.playerGUID);
  const avatar = useAppSelector((state) => state.user.playerAvatar);
  const username = useAppSelector((state) => state.user.playerName);
  const level = useAppSelector((state) => state.user.playerLevel);
  const displayName = useAppSelector((state) => state.user.displayName);

  const players = useTableSelector((table) => table.players);
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const clubId = useTableSelector((table) => table.currentPlayer.clubId);

  const bb = useTableSelector((table) => table.game.bb);
  const card1 = useTableSelector((table) => table.game.card1);
  const card2 = useTableSelector((table) => table.game.card2);
  const card3 = useTableSelector((table) => table.game.card3);
  const card4 = useTableSelector((table) => table.game.card4);
  const card5 = useTableSelector((table) => table.game.card5);
  const hand = useTableSelector((table) => table.game.hand);
  const gameIDFromTable = useTableSelector((table) => table.game.gameID);
  const linkedId = useTableSelector((table) => table.game.tournamentId);
  const highestBetPlayer = useTableSelector((table) => table.game.highestBetPlayer);
  const highestPlayerLBet = convertToNumber(highestBetPlayer?.betDisplay);
  // Preserve the hydrated Redux value's numeric type. The contextual ID is
  // only the pre-hydration fallback and cannot be emitted without gameType.
  const gameID = gameIDFromTable || tableId;

  /**
   * Refuse a table command that cannot be delivered, and report it.
   *
   * `event` is OPT-IN, and it changes what "sendable" means. Without it the
   * check is what it has always been: the manager exists and this panel knows
   * its table. That is a presence check, NOT a liveness one — `socket` is the
   * ConnectionManager proxy, one object created on initialize and refreshed on
   * status change, so it stays truthy while both underlying sockets are down.
   *
   * Passing the event name adds the liveness check, asked of the socket that
   * event actually routes to (see the proxy's canDeliver). Use it where a
   * silently buffered send is worse than a refusal — socket.io queues into
   * `sendBuffer` and the raw adapter into an uncapped queue that survives
   * reconnect, so an emit made on a dead link can arrive minutes later against
   * a hand that has moved on.
   *
   * It is opt-in rather than the default because refusing is not universally
   * safer: `leaveTable` does its local UI work AFTER this guard, so failing it
   * closed would strand the player in a table view they asked to exit. Each
   * call site has to decide, and today only the vote has.
   *
   * Fails OPEN when `canDeliver` is missing: only a test double lacks it, and
   * refusing there would turn every existing socket mock into a rejection.
   */
  const rejectUnsendableTableCommand = useCallback(
    (timeoutCallback?: () => void, event?: string) => {
      if (socket && gameID && gameType) {
        const undeliverable =
          !!event && typeof socket.canDeliver === 'function' && !socket.canDeliver(event);
        if (!undeliverable) return false;

        reportUnsentAction('socket-disconnected', gameID, gameType, timeoutCallback);
        return true;
      }

      reportUnsentAction(
        socket ? 'table-identity-incomplete' : 'socket-unavailable',
        gameID,
        gameType,
        timeoutCallback
      );
      return true;
    },
    [gameID, gameType, socket]
  );

  const baseParams = useMemo(
    () => ({
      gameID: gameID,
      tableID: gameID,
      game: gameType,
      username: !username ? localStorage.getItem('playerName') : username,
      socketId: socket?.id,
      data: '',
      playerGUID: !guid ? localStorage.getItem('playerGUID') : guid,
      level: level,
      seatId: seatId,
      clubId: clubId,
      language: 'en',
      displayName: displayName,
      tournamentId: linkedId,
      uid: '',
      deviceId: getDeviceId()
    }),
    [clubId, displayName, gameID, gameType, guid, level, linkedId, seatId, socket?.id, username]
  );

  const pauseCurrentHandHistorySession = useCallback(
    (excludeRunningHand: boolean) => {
      const sessionTableId = String(tableId || gameID || '');
      const sessionPlayerGUID = String(guid || localStorage.getItem('playerGUID') || '');
      pauseHandHistorySession(sessionTableId, sessionPlayerGUID, { excludeRunningHand });
    },
    [gameID, guid, tableId]
  );

  const ackCallbackWithTimeout = (callback: () => void, timeoutCallback: () => void) => {
    return (msg: any) => {
      if (msg) {
        timeoutCallback();
      } else {
        callback();
      }
    };
  };

  const joinTable = useCallback(
    (gameID: any, gameType: any) => {
      if (!socket) return;

      const params = {
        ...baseParams,
        gameID: gameID,
        game: gameType
      };

      socket.emit(ONLINE, params);

      // ConnectionManager.getProxy() routes 'SubscribeToGameUpdates' to the
      // API Gateway WebSocket. The constant `SUBCRIBE_TO_GAME_UPDATES`
      // resolves to that exact same string, so emitting BOTH (as we used to)
      // delivered two identical subscribe messages. Two concurrent
      // subscribeGame Lambda invocations would then race on the same
      // DynamoDB connection item and lose with `TransactionConflict`,
      // killing the fan-out and producing a stuck "no GOT_TEXAS" page.
      // One emit per subscribe — the lambda only reads gameID + game from
      // the body anyway.
      console.log('[GameService] Subscribing to table via API Gateway:', gameID, gameType);
      socket.emit(SUBCRIBE_TO_GAME_UPDATES, {
        gameID: gameID,
        game: gameType || 'texas',
        playerGUID: guid || localStorage.getItem('playerGUID'),
        username: username || localStorage.getItem('playerName'),
        deviceId: getDeviceId()
      });
    },
    [socket, baseParams, tableId, gameType, guid, username]
  );

  // Returns whether the leave went out on a live link. False when the command
  // could not be sent (no socket, or this panel doesn't know its table), and
  // when the link is down: the proxy stays truthy then and the emit is only
  // buffered, to land on reconnect, if at all. Either way the engine may still
  // seat the player, so callers must not treat false as a durable exit. The
  // local exit still runs on a buffered send — refusing it would strand the
  // player in a table they asked to leave (see rejectUnsendableTableCommand).
  const leaveTable = useCallback(
    (exitFlag = false, immediate = false, pauseHistorySession = false): boolean => {
      // Start the return window even when the leave cannot be sent: callers
      // still navigate away. EN-10944 retains history for a 30-minute return.
      // An unsent leave keeps the player seated in the running hand, so that
      // hand stays theirs rather than being excluded as abandoned.
      const unsendable = rejectUnsendableTableCommand();
      if (pauseHistorySession) {
        pauseCurrentHandHistorySession(!unsendable);
      }
      if (unsendable) return false;
      // Asked before the emit; fails open for a socket double without it.
      const delivered = typeof socket.canDeliver !== 'function' || socket.canDeliver(SITOUT);

      // Unity parity: BuyInView.cs:647 — ShowJoinButton(false) before
      // WaitForSeatAvailability. UserActions re-asserts true once
      // isSeated flips back to false.
      dispatch(showJoinButton(false));

      socket.emit(SITOUT, {
        ...baseParams,
        data: `&leaveGame=true&exitFlag=${exitFlag}&immediate=${immediate}`
      });

      // New requirement, stood up the player when exiting the table.
      // Game tab is not closed but switched to observer mode.
      if (exitFlag) {
        // Prefer `tableId` (URL / TableIdContext) over `baseParams.gameID`
        // for the table being unsubscribed. baseParams.gameID is sourced
        // from `useTableSelector((t) => t.game.gameID)`, which falls back
        // to `state.currentTable` when `state.tables[gameID]` isn't
        // hydrated — and currentTable holds whichever table broadcast last
        // (could be a totally different table from the one the user is
        // leaving). tableId is bound to the URL/context, so it always
        // names the correct table.
        const exitGameID = String(tableId || baseParams.gameID || '');
        const exitGame = baseParams.game || gameType;

        // Record the explicit exit so the GOT_PLAYER_GAMES auto-resub in
        // App.tsx doesn't immediately re-subscribe us while the engine
        // is still processing the leave (especially deferred leaves that
        // wait for the big blind). Entries are pruned on read.
        if (exitGameID) {
          try {
            const stored = JSON.parse(localStorage.getItem('recentlyExitedTables') || '{}');
            stored[exitGameID] = Date.now();
            localStorage.setItem('recentlyExitedTables', JSON.stringify(stored));
          } catch {
            /* noop */
          }
        }

        // Single emit — UNSUBCRIBE_TO_GAME_UPDATES === 'UnsubscribeFromGameUpdates'
        // and ConnectionManager already routes that string to the API Gateway
        // WebSocket. Emitting twice would invoke the unsubscribeGame lambda
        // concurrently and race on the connection's DynamoDB item — same
        // failure mode as the joinTable double-emit.
        socket.emit(UNSUBCRIBE_TO_GAME_UPDATES, {
          ...baseParams,
          gameID: exitGameID,
          tableID: exitGameID,
          game: exitGame
        });
      }
      return delivered;
    },
    [
      baseParams,
      socket,
      tableId,
      gameType,
      dispatch,
      rejectUnsendableTableCommand,
      pauseCurrentHandHistorySession
    ]
  );

  /**
   * Unsubscribe from a table's updates on both sockets.
   * Used when navigating away from a table the player is NOT seated at (observing).
   */
  const unsubscribeTable = useCallback(
    (gameID: string, game: string) => {
      if (!socket) return;
      console.log('[GameService] Unsubscribing from table:', gameID, game);
      // Single emit — see leaveTable comment for the rationale.
      // Override BOTH gameID and tableID so the legacy `baseParams.gameID`
      // (which can lag behind the URL via the useTableSelector fallback)
      // doesn't ship a different value than the table actually being
      // unsubscribed.
      socket.emit(UNSUBCRIBE_TO_GAME_UPDATES, {
        ...baseParams,
        gameID,
        tableID: gameID,
        game
      });
    },
    [baseParams, socket]
  );

  const unregister = useCallback(
    (tournamentId: any, gameID: any) => {
      if (!socket) return;

      socket.emit(GET_TOURNAMENT, {
        ...baseParams,
        action: 'unregister',
        tournamentId,
        game: 'texas',
        gameID
      });
    },
    [baseParams, socket]
  );

  // playerGUID here comes straight from the stable Redux guid (localStorage
  // fallback) — NOT baseParams. baseParams re-memoizes on every seatId/level
  // change (which churn while the tournament seats the player), so depending on
  // it here re-created these callbacks each render and thrashed the subscribe/
  // unsubscribe effects that list them as deps (observed: ms-apart
  // subscribeTournament/unsubscribeTournament pairs that flap the subscription
  // and drop gotTnmStatus pushes in between). socket + guid are both stable.
  //
  // Tournament-feed subscribe/unsubscribe are REF-COUNTED via the module-level
  // tournamentSubCounts map. Many surfaces subscribe the same tournament
  // concurrently (lobby info modal, in-game info panel, per-grid-cell, App-
  // level open-table hold). One `subscribeTournament` on the wire serves all
  // of them, and `unsubscribeTournament` drops the whole connection's fan-out
  // — so a stray unsubscribe from one unmounting surface would kill
  // `gotTnmStatus` for every other holder. Only the LAST release actually
  // tears down the feed, and only the FIRST acquire actually opens it.
  const subscribeTournamentDetails = useCallback(
    (tournamentId: any) => {
      if (!socket) return;
      // Reject non-positive-integer tournamentIds HERE, above the ref-count and
      // above both wire legs (serverless `subscribeTournament` AND legacy
      // `registerSocket {page:'tournament'}`). The ConnectionManager proxy also
      // rejects the serverless leg for defence-in-depth, but the legacy leg
      // routes to the action socket directly and would otherwise carry `-1`
      // into the engine's `tournament-<id>` room. Gating here suppresses both
      // together AND keeps the ref-count clean (no phantom slot for -1).
      if (!isValidTournamentId(tournamentId)) return;
      const playerGUID = guid || localStorage.getItem('playerGUID');

      // Bump the ref-count. Multiple concurrent acquirers share one wire
      // subscription; only the 0→1 transition below fires the actual socket
      // emit.
      const key = String(tournamentId);
      const prevCount = tournamentSubCounts[key] || 0;
      tournamentSubCounts[key] = prevCount + 1;

      // Emit the wire subscribe ONLY on the 0→1 transition. Later acquirers
      // (info modal → observed table, etc.) piggy-back on the existing feed.
      // Without this gate the connection racks up redundant DynamoDB writes
      // and legacy-engine registers for a subscription that already exists.
      if (prevCount > 0) return;

      // Subscribe via API Gateway WebSocket for serverless tournament broadcasts
      socket.emit('subscribeTournament', {
        tournamentId,
        playerGUID
      });
      // Also subscribe via legacy engine for backward compatibility
      socket.emit(REGISTER_SOCKET, {
        page: 'tournament',
        tournamentId: tournamentId,
        isRegister: true
      });
    },
    [socket, guid]
  );

  // Re-assert an EXISTING tournament subscription WITHOUT touching the subscriber
  // ref-count. Needed because the serverless tournament broadcaster fans out to
  // connections in the tournament room (populated by subscribeTournament) and
  // drops any connection a send fails to (cleanupConnection). A single-table
  // navigation — e.g. rebalancing moving the player to a new table — can
  // transiently break the WebSocket send mid-move, so the connection is removed
  // from the room with NO 'disconnect'/'connect' cycle to trigger resubscribeAll.
  // Re-emitting subscribeTournament re-adds the connection to the room. It must
  // NOT go through subscribe/unsubscribeTournamentDetails (those move the count),
  // so the balancing bookkeeping stays correct. Idempotent server-side.
  const resubscribeTournamentDetails = useCallback(
    (tournamentId: any) => {
      if (!socket) return;
      // Same invalid-id guard as subscribe/unsubscribe — reject BEFORE either
      // wire leg so a stale/misfired re-assert for `-1` can't join the room.
      if (!isValidTournamentId(tournamentId)) return;
      const playerGUID = guid || localStorage.getItem('playerGUID');
      socket.emit('subscribeTournament', { tournamentId, playerGUID });
      socket.emit(REGISTER_SOCKET, {
        page: 'tournament',
        tournamentId,
        isRegister: true
      });
    },
    [socket, guid]
  );

  const unsubscribeTournamentDetails = useCallback(
    (tournamentId: any, page = 'tournament') => {
      if (!socket) return;
      // Reject invalid tournamentIds ABOVE the ref-count and the wire legs (see
      // subscribeTournamentDetails). An invalid id can never have been counted
      // (subscribe rejects it too), so the release is a no-op regardless.
      if (!isValidTournamentId(tournamentId)) return;
      const playerGUID = guid || localStorage.getItem('playerGUID');

      // Decrement the ref-count. Only tear down the shared tournament feed on
      // the 1→0 transition — other surfaces holding the same tournament stay
      // subscribed via the shared feed.
      const key = String(tournamentId);
      const prev = tournamentSubCounts[key] || 0;
      if (prev <= 0) return; // nothing subscribed → nothing to tear down
      const next = prev - 1;
      tournamentSubCounts[key] = next;
      if (next > 0) return; // other holders still need the feed

      // Last holder released → tear down IMMEDIATELY. The old 30 s deferred
      // teardown was in place for the pre-set-based architecture, when a
      // balancing move fired per-table unsubscribe/subscribe pairs a tick
      // apart. Now the App-level subscribe path is SET-BASED — an in-tournament
      // balance keeps the joined subscribe-set string stable across the move
      // (both A and B carry the same tournamentId), so the diff-effect doesn't
      // fire mid-move in the first place. Closing an info modal or leaving a
      // tournament with no other holders now cleans up right away.
      delete tournamentSubCounts[key];
      // Unsubscribe from API Gateway WebSocket
      socket.emit('unsubscribeTournament', { tournamentId, playerGUID });
      // Also unsubscribe from legacy engine
      socket.emit(LEAVE_ROOM, { page: page, tournamentId: tournamentId });
    },
    [socket, guid]
  );

  const register = useCallback(
    (tournamentId: any, gameID: any) => {
      if (!socket) return;

      socket.volatile.emit(GET_TOURNAMENT, {
        ...baseParams,
        action: 'register',
        tournamentId,
        game: 'texas',
        gameID
      });
    },
    [baseParams, socket]
  );

  const structures = useCallback(
    (tournamentId: any) => {
      if (!socket) return;

      socket.emit(GET_TOURNAMENT, {
        ...baseParams,
        action: 'structures',
        tournamentId,
        game: 'texas'
      });
    },
    [baseParams, socket]
  );

  const tournamentPrizes = useCallback(
    (tournamentId: any) => {
      if (!socket) return;

      socket.emit(GET_TOURNAMENT, {
        ...baseParams,
        action: 'prizes',
        tournamentId,
        game: 'texas'
      });
    },
    [baseParams, socket]
  );

  const sitOut = useCallback(() => {
    if (rejectUnsendableTableCommand()) return;

    socket.emit(SITOUT, {
      ...baseParams,
      data: '&leaveGame=false'
    });
  }, [baseParams, rejectUnsendableTableCommand, socket]);

  const imBack = useCallback(
    (seatId: number) => {
      if (rejectUnsendableTableCommand()) return;

      socket.emit(RETURN_TABLE, {
        ...baseParams,
        data: `&pos=${seatId}`
      });
    },
    [baseParams, rejectUnsendableTableCommand, socket]
  );

  const sitDown = useCallback(
    (targetTableId: any, seatId: any, amount: any, gameType?: any) => {
      if (!socket) return;

      // `targetTableId` used to be accepted and then silently dropped: the
      // emit went out with `baseParams.gameID`, which comes from
      // `useTableSelector` and therefore reads 0 while this table's slice is
      // unhydrated and the LAST-BROADCAST table when TableIdContext is empty.
      // The engine seats you at whatever gameID it receives
      // (userJoinGame -> findTableWithWaitlist -> WHERE p.gameID = ?), so
      // with several tables open that mis-targeted the join — and when the
      // resolved table was one the player already sat at, the engine answered
      // "You are already seated!" while the felt in front of them still showed
      // JOIN TABLE. The GAME TYPE travels explicitly for the same reason —
      // the engine routes and queries with it, and a stale one is a silent
      // no-op server-side. See services/joinGameParams.ts for all three rules.
      const params = buildJoinGameParams(baseParams, targetTableId, seatId, amount, gameType);
      // EN-11130: refuse, visibly, while the action socket is down. A plain
      // emit on a dead link is queued in socket.io's sendBuffer and flushed on
      // reconnect: live table 2847 received six buy-ins tapped during a 56 s
      // outage in one 359 ms burst and seated the player twice. A refusal the
      // player can retry beats a queued buy-in that lands after they moved on.
      // Liveness only (not rejectUnsendableTableCommand): the join's identity is
      // `targetTableId`, which this panel's own gameID may not match yet.
      if (typeof socket.canDeliver === 'function' && !socket.canDeliver(GET_JOINGAME)) {
        reportUnsentAction('socket-disconnected', String(params.gameID), String(params.game));
        dispatch({
          ...setSeatJoinError({ message: JOIN_OFFLINE_MESSAGE, seatId, at: Date.now() }),
          meta: { gameID: params.gameID }
        });
        return;
      }
      // A PLAIN emit, deliberately NOT volatile. Volatile packets are dropped
      // whenever the transport is not writable at that instant, and rapid
      // table switching floods this very socket with REGISTER_SOCKET /
      // presence / resub writes — so a buy-in clicked right after switching
      // could be discarded on the client without ever reaching the engine:
      // no error, no gotJoinGame, the modal just did nothing (Marlon QA +
      // owner repro, 8/25). A join moves money; it must never ride a
      // droppable transport class.
      // Identity only — params.data carries the buy-in amount, which does not
      // belong in a console at all (SQE scan, Marlon 8/25 HIGH). Dev-gated on
      // top of that: this is a money-moving path and the emit does not need to
      // narrate itself in production (JRam review, #3721).
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[GameService] joinGame emit:', params.gameID, params.game);
      }
      socket.emit(GET_JOINGAME, params);
      // The engine's "Seat taken!" rejection names no table; remember which
      // one this request was for so App.tsx can show the error on that felt.
      rememberSeatJoin(params.gameID, seatId);
    },
    [baseParams, dispatch, socket]
  );

  const verifyPrivateTablePassword = useCallback(
    (password: any) => {
      if (rejectUnsendableTableCommand()) return;

      const params = {
        ...baseParams,
        data: `&password=${password}`
      };
      socket.volatile.emit(VERIFY_PRIVATE_TABLE_PASSWORD, params);
    },
    [baseParams, rejectUnsendableTableCommand, socket]
  );

  const rebuy = useCallback(
    (seatId: any, amount: any, isRebuyFlag: boolean) => {
      if (rejectUnsendableTableCommand()) return;

      // The cash "Add Chips" top-up flows through here (engine param is literally
      // `&addon=`), and the engine answers with the same gotTnmAddon success push
      // as the tournament add-on. Record the initiating table so only that cell's
      // AddOnChipsModal shows the success in multi-table view (see addOnInitiator).
      markAddOnInitiated(String(tableId || gameID || ''));

      const params = {
        ...baseParams,
        seatId: seatId,
        data: `&addon=${amount}&pos=${seatId}&isRebuyFlag=${isRebuyFlag}`
      };

      socket.emit(GET_PLAYERMOVE, params, () => {});
    },
    [baseParams, socket, tableId, gameID, rejectUnsendableTableCommand]
  );

  const addOn = useCallback(() => {
    if (rejectUnsendableTableCommand()) return;

    // Record which table is performing the add-on. The engine's gotTnmAddon
    // success push carries no gameID, so this lets only the initiating cell's
    // AddOnChipsModal show the success in multi-table view (see addOnInitiator).
    markAddOnInitiated(String(tableId || gameID || ''));

    const params = {
      ...baseParams,
      seatId: seatId,
      action: 'addon',
      pos: seatId,
      playerGUID: baseParams.playerGUID,
      game: 'texas',
      gameID
    };

    socket.volatile.emit(GET_TOURNAMENT, params);
  }, [baseParams, socket, tableId, gameID, seatId, rejectUnsendableTableCommand]);

  const tournamentRebuy = useCallback(
    (pSeatId: any, pGameID: any, pTournamentId: any) => {
      if (!socket) return;

      pSeatId = seatId === -1 ? pSeatId : seatId;
      pGameID = gameID === 0 ? pGameID : gameID;
      pTournamentId = +linkedId === 0 ? pTournamentId : linkedId;
      const params = {
        ...baseParams,
        seatId: seatId,
        action: 'rebuy',
        pos: pSeatId,
        playerGUID: baseParams.playerGUID,
        game: 'texas',
        gameID: pGameID,
        tournamentId: pTournamentId
      };

      socket.volatile.emit(GET_TOURNAMENT, params);
    },
    [baseParams, socket]
  );

  const showCard = useCallback(() => {
    if (rejectUnsendableTableCommand()) return;

    const params = {
      ...baseParams,
      seatId: seatId,
      data: '&showcards=true'
    };

    socket.emit(GET_PLAYERMOVE, params, () => {});
  }, [baseParams, rejectUnsendableTableCommand, socket]);

  const muckCard = useCallback(() => {
    if (rejectUnsendableTableCommand()) return;

    const params = {
      ...baseParams,
      seatId: seatId,
      data: '&muckcards=true'
    };

    socket.emit(GET_PLAYERMOVE, params, () => {});
  }, [baseParams, rejectUnsendableTableCommand, socket]);

  // Betting actions carry `turnStamp` (utils/actionOutcome), the turn the player
  // acted on, so the engine can refuse a copy that outlives it (EN-11171).
  const fold = useCallback(
    (turnStamp: string, ackCallback: () => void, timeoutCallback: () => void) => {
      if (rejectUnsendableTableCommand(timeoutCallback)) return;

      const params = {
        ...baseParams,
        seatId: seatId,
        data: '&action=fold&opt=&actionTrigger=' + turnStamp
      };
      socket
        .timeout(EMIT_TIMEOUT)
        .emit(GET_PLAYERMOVE, params, ackCallbackWithTimeout(ackCallback, timeoutCallback));
    },
    [baseParams, rejectUnsendableTableCommand, socket]
  );

  const check = useCallback(
    (turnStamp: string, ackCallback: () => void, timeoutCallback: () => void) => {
      if (rejectUnsendableTableCommand(timeoutCallback)) return;

      const params = {
        ...baseParams,
        seatId: seatId,
        data: '&action=check&opt=&actionTrigger=' + turnStamp
      };
      socket
        .timeout(EMIT_TIMEOUT)
        .emit(GET_PLAYERMOVE, params, ackCallbackWithTimeout(ackCallback, timeoutCallback));
    },
    [baseParams, rejectUnsendableTableCommand, socket]
  );

  const call = useCallback(
    (turnStamp: string, ackCallback: () => void, timeoutCallback: () => void) => {
      if (rejectUnsendableTableCommand(timeoutCallback)) return;

      const params = {
        ...baseParams,
        seatId: seatId,
        data: '&action=call&opt=&actionTrigger=' + turnStamp
      };
      socket
        .timeout(EMIT_TIMEOUT)
        .emit(GET_PLAYERMOVE, params, ackCallbackWithTimeout(ackCallback, timeoutCallback));
    },
    [baseParams, rejectUnsendableTableCommand, socket]
  );

  const raise = (
    turnStamp: string,
    amount: any,
    ackCallback: () => void,
    timeoutCallback: () => void
  ) => {
    if (rejectUnsendableTableCommand(timeoutCallback)) return;

    const action = highestPlayerLBet === 0 ? 'bet' : 'raise';
    const params = {
      ...baseParams,
      seatId: seatId,
      data: '&action=' + action + '&betamount=' + amount.toNumber() + '&type=' + action + turnStamp
    };
    socket
      .timeout(EMIT_TIMEOUT)
      .emit(GET_PLAYERMOVE, params, ackCallbackWithTimeout(ackCallback, timeoutCallback));
  };

  const allIn = (
    turnStamp: string,
    amount: any,
    ackCallback: () => void,
    timeoutCallback: () => void
  ) => {
    if (rejectUnsendableTableCommand(timeoutCallback)) return;

    const params = {
      ...baseParams,
      seatId: seatId,
      data: '&action=allin&betamount=' + amount + turnStamp
    };
    socket
      .timeout(EMIT_TIMEOUT)
      .emit(GET_PLAYERMOVE, params, ackCallbackWithTimeout(ackCallback, timeoutCallback));
  };

  const checkChips = () => {
    if (!socket) return;

    if (!openAddChips && seatId > 0 && players[seatId].bet === 'J0' && players[seatId].stack < bb)
      setOpenAddChips(true);
  };

  const getTableCards = useCallback(() => {
    if (!socket) return;

    switch (hand) {
      case '':
      case '1':
        setTableCards([]);
        break;
      case '6':
        setTableCards([...tableCards, card1, card2, card3]);
        break;
      case '8':
        setTableCards([...tableCards, card4]);
        break;
      case '10':
        setTableCards([...tableCards, card5]);
        break;
      case '15':
      case '16': // hand ended by fold — engine sets hand=16 before resetting to 1
        setTableCards([]);
        break;
      default:
        break;
    }
  }, [card1, card2, card3, card4, card5, hand, setTableCards, tableCards]);

  const postBlind = useCallback(() => {
    if (rejectUnsendableTableCommand()) return;

    const params = {
      ...baseParams
    };
    socket.emit(POST_BLIND, params);
    setIsBlindPosted(true);
  }, [baseParams, rejectUnsendableTableCommand, setIsBlindPosted, socket]);

  const waitForBB = useCallback(() => {
    if (rejectUnsendableTableCommand()) return;

    const params = {
      ...baseParams
    };
    socket.emit(WAIT_FOR_BB, params);
  }, [baseParams, rejectUnsendableTableCommand, socket]);

  /**
   * Cast this seat's run-it-twice vote.
   *
   * `timeoutCallback` is how a refused send becomes visible. It is the ONLY
   * feedback channel this command has: the emit below is fire-and-forget, so
   * unlike fold/check/call/raise there is no ack timeout to fall back on. It
   * used to be omitted entirely, which made a vote cast on a dead socket a
   * console warning and nothing else — the player tapped during a window only
   * seconds long and absolutely nothing happened on screen.
   */
  const multiRunVote = (vote: string, timeoutCallback?: () => void) => {
    // One vote per seat is the server's rule and both writers drop a second
    // one. Checked FIRST so a duplicate tap is not reported as an unsent
    // command — it is a no-op, not a delivery failure.
    if (playerMultiRunVote !== '0') return;

    // Named event, so the guard asks the sockets this emit will actually use
    // (it is dual-emit: either leg can carry it). The vote window is seconds
    // long and NEITHER writer has a reply channel, so a vote handed to a
    // transport buffer is a vote lost — and the optimistic write below would
    // have disabled all three buttons for the rest of that window on its
    // behalf, leaving the player watching "Waiting on other players..." for a
    // vote that never left the device.
    if (rejectUnsendableTableCommand(timeoutCallback, VOTE_MULTI_RUN)) return;

    const params = {
      ...baseParams,
      data: `&vote=${vote}`
    };
    socket.emit(VOTE_MULTI_RUN, params);

    // Optimistic, and only AFTER the send was accepted. The buttons disable off
    // this value, so setting it ahead of the send rendered an undeliverable
    // vote as cast AND locked out every retry through the gate above. Still
    // optimistic because there is no ack to wait for — Game.tsx reconciles it
    // against the server's own echo (reconcileVote), which is the only positive
    // evidence this path has.
    setPlayerMultiRunVote(vote);
  };

  /**
   * EN-11023: take (`true`) or turn off (`false`) the straddle for hand
   * `gameNo` (the offer's), as a pre-action. Goes to the game-ws
   * `straddle_PreAction` route, which reads the seat from the authenticated
   * connection and refuses a tap that arrives after that hand was dealt. There
   * is no reply: the next broadcast's `pokerActions.straddlePreAction.set`
   * confirms it.
   *
   * Returns whether the command was handed to the socket, so the caller only
   * shows its optimistic state for a send that actually left.
   */
  const straddlePreAction = useCallback(
    (take: boolean, gameNo: number, timeoutCallback?: () => void): boolean => {
      if (rejectUnsendableTableCommand(timeoutCallback, STRADDLE_PRE_ACTION)) return false;
      socket.emit(STRADDLE_PRE_ACTION, {
        ...baseParams,
        data: `&straddle=${take ? 1 : 0}&gameNo=${gameNo}`
      });
      return true;
    },
    [baseParams, rejectUnsendableTableCommand, socket]
  );

  const textChat = (message: string) => {
    if (rejectUnsendableTableCommand()) return;

    const params = {
      ...baseParams,
      avatar,
      displayName,
      data: `&txt=${encode(message)}`
    };

    socket.emit(TEXT_CHAT, params);
  };

  // `readThrough` is the newest message the player has on screen. The server
  // records it as the read watermark, so a late increment for a message at or
  // before it can't bring the badge back, and a newer unread one isn't wiped
  // (EN-10782). The legacy engine ignores it.
  const clearUnreadMessage = (
    game: any,
    gameID: any,
    playerGUID: any,
    readThrough?: ReadThrough
  ) => {
    // Clear this table's badge locally right away so it reflects the read
    // immediately, independent of when the next server broadcast lands.
    dispatch(clearLiveChatNotification({ gameID, ...readThrough }));

    if (!socket) return;

    const params = {
      game,
      gameID,
      playerGUID,
      ...readThrough
    };

    socket.emit(CLEAR_UNREAD_MESSAGE, params);
  };

  const resetMttInactivity = () => {
    if (rejectUnsendableTableCommand()) return;

    const params = {
      ...baseParams,
      tableType: 'm'
    };

    socket.emit(RESET_MTT_INACTIVITY, params);
  };

  const preselectAction = (action: any, ackCallback: () => void, timeoutCallback: () => void) => {
    if (rejectUnsendableTableCommand(timeoutCallback)) return;

    const params = {
      ...baseParams,
      data: '&action=' + action
    };
    socket
      .timeout(EMIT_TIMEOUT)
      .emit(PRESELECT_ACTION, params, ackCallbackWithTimeout(ackCallback, timeoutCallback));
  };

  return {
    joinTable,
    leaveTable,
    unsubscribeTable,
    sitOut,
    imBack,
    sitDown,
    rebuy,
    addOn,
    showCard,
    muckCard,
    fold,
    check,
    call,
    raise,
    allIn,
    straddlePreAction,
    checkChips,
    getTableCards,
    postBlind,
    multiRunVote,
    waitForBB,
    textChat,
    clearUnreadMessage,
    tournamentRebuy,
    unregister,
    register,
    structures,
    tournamentPrizes,
    subscribeTournamentDetails,
    unsubscribeTournamentDetails,
    resubscribeTournamentDetails,
    resetMttInactivity,
    preselectAction,
    verifyPrivateTablePassword
  };
};

export default useGameService;
